/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React, { Component } from 'react';
import PropTypes from "prop-types";
import moment from 'moment'; // eslint-disable-line import/named

import dateMath from '@elastic/datemath';
import { keys } from '../../../../services';
import { EuiFormRow, EuiFieldText, EuiFormLabel } from '../../../form';
import { EuiCode } from '../../../code';
import { EuiI18n } from '../../../i18n';
import { EuiDatePicker } from '../../date_picker';
// Allow users to paste in and have the datepicker parse multiple common date formats,
// in addition to the configured displayed `dateFormat` prop
var ALLOWED_USER_DATE_FORMATS = [moment.ISO_8601, moment.RFC_2822, 'X' // Unix timestamp in seconds
];

export class EuiAbsoluteTab extends Component {
  state;
  isParsing = false; // Store outside of state as a ref for faster/unbatched updates

  constructor(props) {
    super(props);
    const parsedValue = dateMath.parse(props.value, {
      roundUp: props.roundUp
    });
    const valueAsMoment = parsedValue && parsedValue.isValid() ? parsedValue : moment();
    const textInputValue = valueAsMoment.locale(this.props.locale || 'en').format(this.props.dateFormat);
    this.state = {
      hasUnparsedText: false,
      isTextInvalid: false,
      textInputValue,
      valueAsMoment
    };
  }
  handleChange = date => {
    const {
      onChange
    } = this.props;
    if (date === null) {
      return;
    }
    onChange(date.toISOString());
    const valueAsMoment = moment(date);
    this.setState({
      valueAsMoment,
      textInputValue: valueAsMoment.format(this.props.dateFormat),
      hasUnparsedText: false,
      isTextInvalid: false
    });
  };
  handleTextChange = event => {
    if (this.isParsing) return;
    this.setState({
      textInputValue: event.target.value,
      hasUnparsedText: true,
      isTextInvalid: false
    });
  };
  parseUserDateInput = textInputValue => {
    this.isParsing = true;
    // Wait a tick for state to finish updating (whatever gets returned),
    // and then allow `onChange` user input to continue setting state
    requestAnimationFrame(() => {
      this.isParsing = false;
    });
    const invalidDateState = {
      textInputValue,
      isTextInvalid: true,
      valueAsMoment: null
    };
    if (!textInputValue) {
      return this.setState(invalidDateState);
    }
    const {
      onChange,
      dateFormat,
      locale
    } = this.props;

    // Attempt to parse with passed `dateFormat` and `locale`
    let valueAsMoment = moment(textInputValue, dateFormat, typeof locale === 'string' ? locale : 'en',
    // Narrow the union type to string
    true);
    let dateIsValid = valueAsMoment.isValid();

    // If not valid, try a few other other standardized formats
    if (!dateIsValid) {
      valueAsMoment = moment(textInputValue, ALLOWED_USER_DATE_FORMATS, true);
      dateIsValid = valueAsMoment.isValid();
    }
    if (dateIsValid) {
      onChange(valueAsMoment.toISOString());
      this.setState({
        textInputValue: valueAsMoment.format(this.props.dateFormat),
        valueAsMoment: valueAsMoment,
        hasUnparsedText: false,
        isTextInvalid: false
      });
    } else {
      this.setState(invalidDateState);
    }
  };
  render() {
    const {
      dateFormat,
      timeFormat,
      locale,
      utcOffset,
      labelPrefix
    } = this.props;
    const {
      valueAsMoment,
      isTextInvalid,
      hasUnparsedText,
      textInputValue
    } = this.state;
    return <>
        <EuiDatePicker inline showTimeSelect shadow={false} selected={valueAsMoment} onChange={this.handleChange} dateFormat={dateFormat} timeFormat={timeFormat} locale={locale} utcOffset={utcOffset} />
        <EuiI18n tokens={['euiAbsoluteTab.dateFormatHint', 'euiAbsoluteTab.dateFormatError']} defaults={['Press the Enter key to parse as a date.', 'Allowed formats: {dateFormat}, ISO 8601, RFC 2822, or Unix timestamp.']} values={{
        dateFormat: <EuiCode>{dateFormat}</EuiCode>
      }}>
          {([dateFormatHint, dateFormatError]) => <EuiFormRow className="euiSuperDatePicker__absoluteDateFormRow" isInvalid={isTextInvalid} error={isTextInvalid ? dateFormatError : undefined} helpText={hasUnparsedText ? isTextInvalid ? dateFormatHint : [dateFormatHint, dateFormatError] : undefined}>
              <EuiFieldText compressed isInvalid={isTextInvalid} value={textInputValue} onChange={this.handleTextChange} onPaste={event => {
            this.parseUserDateInput(event.clipboardData.getData('text'));
          }} onKeyDown={event => {
            if (event.key === keys.ENTER) {
              this.parseUserDateInput(textInputValue);
            }
          }} data-test-subj="superDatePickerAbsoluteDateInput" prepend={<EuiFormLabel>{labelPrefix}</EuiFormLabel>} />
            </EuiFormRow>}
        </EuiI18n>
      </>;
  }
}
EuiAbsoluteTab.propTypes = {
  dateFormat: PropTypes.string.isRequired,
  timeFormat: PropTypes.string.isRequired,
  locale: PropTypes.any,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  roundUp: PropTypes.bool.isRequired,
  position: PropTypes.oneOf(["start", "end"]).isRequired,
  labelPrefix: PropTypes.string.isRequired,
  utcOffset: PropTypes.number
};
try {
  EuiAbsoluteTab.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/date_picker/super_date_picker/date_popover/absolute_tab.tsx',
    description: '',
    displayName: 'EuiAbsoluteTab',
    methods: [],
    props: {
      dateFormat: {
        defaultValue: null,
        description: '',
        name: 'dateFormat',
        parent: {
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/absolute_tab.tsx',
          name: 'EuiAbsoluteTabProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/absolute_tab.tsx',
          name: 'EuiAbsoluteTabProps'
        }],
        required: true,
        type: {
          name: 'string'
        }
      },
      timeFormat: {
        defaultValue: null,
        description: '',
        name: 'timeFormat',
        parent: {
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/absolute_tab.tsx',
          name: 'EuiAbsoluteTabProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/absolute_tab.tsx',
          name: 'EuiAbsoluteTabProps'
        }],
        required: true,
        type: {
          name: 'string'
        }
      },
      locale: {
        defaultValue: null,
        description: '',
        name: 'locale',
        parent: {
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/absolute_tab.tsx',
          name: 'EuiAbsoluteTabProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/absolute_tab.tsx',
          name: 'EuiAbsoluteTabProps'
        }],
        required: false,
        type: {
          name: 'LocaleSpecifier'
        }
      },
      value: {
        defaultValue: null,
        description: '',
        name: 'value',
        parent: {
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/absolute_tab.tsx',
          name: 'EuiAbsoluteTabProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/absolute_tab.tsx',
          name: 'EuiAbsoluteTabProps'
        }],
        required: true,
        type: {
          name: 'string'
        }
      },
      onChange: {
        defaultValue: null,
        description: '',
        name: 'onChange',
        parent: {
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/absolute_tab.tsx',
          name: 'EuiAbsoluteTabProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/absolute_tab.tsx',
          name: 'EuiAbsoluteTabProps'
        }],
        required: true,
        type: {
          name: '(date: string) => void'
        }
      },
      roundUp: {
        defaultValue: null,
        description: '',
        name: 'roundUp',
        parent: {
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/absolute_tab.tsx',
          name: 'EuiAbsoluteTabProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/absolute_tab.tsx',
          name: 'EuiAbsoluteTabProps'
        }],
        required: true,
        type: {
          name: 'boolean'
        }
      },
      position: {
        defaultValue: null,
        description: '',
        name: 'position',
        parent: {
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/absolute_tab.tsx',
          name: 'EuiAbsoluteTabProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/absolute_tab.tsx',
          name: 'EuiAbsoluteTabProps'
        }],
        required: true,
        type: {
          name: 'enum',
          raw: '"end" | "start"',
          value: [{
            value: '"end"'
          }, {
            value: '"start"'
          }]
        }
      },
      labelPrefix: {
        defaultValue: null,
        description: '',
        name: 'labelPrefix',
        parent: {
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/absolute_tab.tsx',
          name: 'EuiAbsoluteTabProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/absolute_tab.tsx',
          name: 'EuiAbsoluteTabProps'
        }],
        required: true,
        type: {
          name: 'string'
        }
      },
      utcOffset: {
        defaultValue: null,
        description: '',
        name: 'utcOffset',
        parent: {
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/absolute_tab.tsx',
          name: 'EuiAbsoluteTabProps'
        },
        declarations: [{
          fileName: 'app/src/components/date_picker/super_date_picker/date_popover/absolute_tab.tsx',
          name: 'EuiAbsoluteTabProps'
        }],
        required: false,
        type: {
          name: 'number'
        }
      }
    },
    extendedInterfaces: ['EuiAbsoluteTabProps']
  };
} catch (e) {}